import { ModalButtons } from "components/Modal/ModalButtons";

export const hasBeenEdited = (published, draft, field) => {
	const publishedField = (field) => published?.[field] ?? "";
	try {
		return draft[field]() !== publishedField(field);
	}
	catch (error) {
		console.error(error, field);
	}
};

/**
 * Handle keydown for single-line name input.
 */
export const handleSingleLineInputKeyDown = (event, cancelAll) => {
	if (event.key === "Escape") {
		cancelAll();
	}
	else if (event.key === "Enter") {
		event.currentTarget.blur();
	}
};

/**
 * Handle keydown for multiline description input.
 */
export const handleMultilineInputKeyDown = (event, cancelAll) => {
	if (event.key === "Escape") {
		cancelAll();
		//Shift key to allow user to go to new line if needed
	}
	else if (event.key === "Enter" && !event.shiftKey) {
		event.preventDefault();
		event.currentTarget.blur();
	}
};

/**
 * Open the hidden file input used for image picking.
 */
export const openFilePicker = (fileInputRef) => {
	if (fileInputRef.current) {
		fileInputRef.current.value = "";
		fileInputRef.current.click();
	}
};

const encodeValidationErrors = (validationErrors) => {
	const errorListItems = validationErrors.map((x) => {
		return <li key={x}>{x}</li>;
	});
	return <ul>{errorListItems}</ul>;
};

/**
 * Validate and upload a course image file, then commit into the draft.
 */
export const handleImageFileChange = (e, fileInputRef, openModal, onCommit) => {
	const file = (e.target.files && e.target.files[0]) || null;
	if (!file) {
		return;
	}

	const validationErrors = [];

	// Size check (< 300 KB)
	if (file.size >= 300 * 1024) {
		validationErrors.push("File must be smaller than 300 KB.");
	}

	// Validate dimensions/aspect using an object URL
	const objectUrl = URL.createObjectURL(file);
	const img = new Image();

	img.onload = () => {
		try {
			const w = img.naturalWidth || img.width;
			const h = img.naturalHeight || img.height;

			// Max width 1200px
			if (w > 1200) {
				validationErrors.push("Image width must be 1200px or less.");
			}

			// Aspect ratio: height between 60% and 100% of width (0.6 to 1.0)
			const ratio = h / w;
			if (ratio < 0.6 || ratio > 1.0) {
				validationErrors.push(
					"Image height must be between 60% and 100% of its width (5:3 up to square)."
				);
			}
		}
		finally {
			URL.revokeObjectURL(objectUrl);
		}

		// If any validation failed, show modal and exit
		if (validationErrors.length > 0) {
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			openModal(
				null,
				<div>We could not use that image:<br />{encodeValidationErrors(validationErrors)}</div>,
				ModalButtons.Ok
			);
			return;
		}

		// All validations passed -> proceed
		const reader = new FileReader();
		reader.onload = () => {
			const fileData = {
				FileName: file.name,
				MimeType: file.type,
				Base64: reader.result
			};
			onCommit(fileData);
		};
		reader.onerror = () => {
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			console.error("Image upload/convert error");
			openModal(
				null,
				"We could not read that image file.",
				ModalButtons.YesNo
			);
		};

		reader.readAsDataURL(file);
	};

	img.onerror = () => {
		URL.revokeObjectURL(objectUrl);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
		openModal(
			null,
			"We could not load that image to validate it.",
			ModalButtons.YesNo
		);
	};

	img.src = objectUrl;
};
